// ClientAvatar — foto del cliente de lealtad (módulo PERFIL compartido).
// Por defecto muestra la INICIAL (letra); si el cliente tiene foto la muestra; al tocar
// abre un menú para Tomar foto (cámara) o Subir de galería. La imagen se comprime a 512px
// antes de enviarla (límite ~1mb del body de lealify-base) y se guarda vía POST /contacts/:id/avatar.
// Gemelo del PatientAvatar de la agenda, con los tokens de tema del manager.
import React, { useState, useRef } from 'react';
import { Camera, Image as ImageIcon, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { clientsApi } from '../../api/clientsApi';

// Comprime una imagen a un dataURL pequeño (máx 512px + JPEG 0.85) por el límite ~1mb del body.
function fileToCompressedDataUrl(file: File, max = 512, quality = 0.85): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('No se pudo leer la imagen'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Imagen inválida'));
      img.onload = () => {
        let { width, height } = img;
        if (width >= height && width > max) { height = Math.round(height * max / width); width = max; }
        else if (height > max) { width = Math.round(width * max / height); height = max; }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('No se pudo procesar'));
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}

interface ClientAvatarProps {
  clientId: string;
  name: string | null;
  hasPhoto?: boolean;          // si la ficha ya trae avatar_url, arranca mostrando la foto (sin flash de 404)
  onUploaded?: () => void;     // callback tras subir (para refrescar la ficha si hace falta)
}

const ClientAvatar: React.FC<ClientAvatarProps> = ({ clientId, name, hasPhoto = false, onUploaded }) => {
  const [ver, setVer] = useState(0);                 // bust de caché tras subir
  const [failed, setFailed] = useState(!hasPhoto);   // sin foto → muestra inicial sin pedir img
  const [uploading, setUploading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const cameraRef = useRef<HTMLInputElement>(null);   // input con cámara
  const galleryRef = useRef<HTMLInputElement>(null);  // input con galería
  const initial = (name || '?').trim().charAt(0).toUpperCase() || '?';
  const src = clientsApi.avatarSrc(clientId, ver);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await fileToCompressedDataUrl(file);
      await clientsApi.uploadAvatar(clientId, dataUrl);
      setFailed(false); setVer(Date.now());
      toast.success('Foto del cliente actualizada');
      onUploaded?.();
    } catch (err: any) {
      toast.error(err?.message || 'No se pudo subir la foto');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="relative flex-shrink-0">
      <button
        onClick={() => setMenuOpen((o) => !o)}
        disabled={uploading}
        className="relative h-12 w-12 rounded-full active:scale-95 transition-transform focus:outline-none focus:ring-2 focus:ring-accent"
        title="Foto del cliente"
      >
        {!failed ? (
          <img src={src} alt={name || 'Cliente'} onError={() => setFailed(true)} className="h-12 w-12 rounded-full object-cover" />
        ) : (
          <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center text-accent text-xl font-bold">{initial}</div>
        )}
        <span className="absolute -bottom-0.5 -right-0.5 bg-accent text-white rounded-full p-1 shadow">
          {uploading ? <Loader2 className="animate-spin h-3 w-3" /> : <Camera className="h-3 w-3" />}
        </span>
      </button>

      {menuOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
          <div className="absolute z-20 left-0 top-14 w-48 bg-surface border border-line rounded-lg shadow-lg py-1">
            <button
              onClick={() => { setMenuOpen(false); cameraRef.current?.click(); }}
              className="w-full text-left px-3 py-2 text-sm text-content-2 hover:bg-surface-2 flex items-center gap-2"
            >
              <Camera className="h-4 w-4" /> Tomar foto
            </button>
            <button
              onClick={() => { setMenuOpen(false); galleryRef.current?.click(); }}
              className="w-full text-left px-3 py-2 text-sm text-content-2 hover:bg-surface-2 flex items-center gap-2"
            >
              <ImageIcon className="h-4 w-4" /> Subir de galería
            </button>
          </div>
        </>
      )}

      {/* La foto del cliente se TOMA con la cámara trasera (capture=environment); galería = sin capture. */}
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} />
      <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={onPick} />
    </div>
  );
};

export default ClientAvatar;
